import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTheme, fonts } from "@/theme";
import { Icon } from "@/components/Icon";
import { CrystalVisual } from "@/components/CrystalVisual";
import { Screen, TopBar, Loader } from "@/components/ui";
import { crystalsApi } from "@/data/api";

export default function Library() {
  const { colors } = useTheme();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const title = params.get("title") || "Crystal Library";
  const has = params.get("has") || "";
  const intention = params.get("intention") || "";
  const [search, setSearch] = useState(params.get("search") || "");
  const [letter, setLetter] = useState(params.get("letter") || "");
  const [letters, setLetters] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    crystalsApi.meta().then((m) => setLetters(m.letters || [])).catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    crystalsApi.list({ search: search.trim(), letter, intention, has, limit: 100 })
      .then((r) => setItems(r.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [search, letter, intention, has]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const pickLetter = (l) => {
    const next = letter === l ? "" : l;
    setLetter(next);
    const p = new URLSearchParams(params);
    if (next) p.set("letter", next); else p.delete("letter");
    setParams(p, { replace: true });
  };

  return (
    <Screen>
      <TopBar title={title} />
      <div className="ritzy-fade" style={{ padding: "16px 20px 40px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, background: colors.surfaceSecondary, border: `1px solid ${colors.border}`, borderRadius: 14, padding: "0 14px", height: 48, marginBottom: 12 }}>
          <Icon name="search" size={18} color={colors.muted} />
          <input data-testid="library-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search crystals..." style={{ flex: 1, border: "none", outline: "none", background: "transparent", color: colors.onSurface, fontSize: 15, fontFamily: fonts.text }} />
          {search ? <button onClick={() => setSearch("")} style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }}><Icon name="x" size={18} color={colors.muted} /></button> : null}
        </div>

        {intention ? <div style={{ display: "inline-flex", alignItems: "center", gap: 6, background: colors.brandTertiary, color: colors.onBrandTertiary, borderRadius: 999, padding: "6px 12px", marginBottom: 12, fontSize: 13, fontWeight: 700, fontFamily: fonts.text }}>Intention: {intention}<button onClick={() => navigate("/crystal-school/library")} style={{ background: "none", border: "none", cursor: "pointer", display: "flex" }}><Icon name="x" size={14} color={colors.onBrandTertiary} /></button></div> : null}

        {letters.length ? (
          <div className="hide-scroll" style={{ display: "flex", gap: 6, overflowX: "auto", paddingBottom: 12 }}>
            {letters.map((l) => {
              const active = letter === l;
              return <button key={l} data-testid={`letter-${l}`} className="pressable" onClick={() => pickLetter(l)} style={{ flexShrink: 0, width: 34, height: 34, borderRadius: 10, border: `1px solid ${active ? colors.brand : colors.border}`, background: active ? colors.brand : colors.surfaceSecondary, color: active ? colors.onBrand : colors.onSurfaceSecondary, fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: fonts.text }}>{l}</button>;
            })}
          </div>
        ) : null}

        {loading ? (
          <Loader />
        ) : items.length === 0 ? (
          <div style={{ padding: 40, textAlign: "center", color: colors.muted, fontFamily: fonts.text }}>No crystals found. Try a different search ✨</div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 12 }}>
            {items.map((c) => (
              <button key={c.id} data-testid={`crystal-card-${c.slug}`} className="pressable" onClick={() => navigate(`/crystal/${c.slug}`)} style={{ border: "none", background: "none", padding: 0, cursor: "pointer" }}>
                <CrystalVisual name={c.name} imageUrl={c.image_url} seed={c.visual_seed} height={130} />
              </button>
            ))}
          </div>
        )}
      </div>
    </Screen>
  );
}
